import React, { useContext, useEffect, useState } from 'react';

import { Box, CircularProgress, Tooltip, useTheme } from '@material-ui/core';
import isEqual from 'lodash/isEqual';
import { MdHistory, MdOutlineMedicalServices, MdOutlinePictureAsPdf } from 'react-icons/md';
import { AxiosResponse } from 'axios';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';

import { GRID_PAGE_SIZE } from '@vyce/core/src/constants';
import { AppDataGrid } from '@vyce/core/src/components';
import { Agreement, AgreementPaySchedule } from '@vyce/core/src/types';
import { formatSortModel } from '@vyce/core/src/utils/sorting';
import { formatTableDate } from '@vyce/core/src/utils/dates';
import { IsVerified } from '@vyce/core/src/components';
import { AppIconButton } from '@vyce/core/src/components';
import { generateFileLinkAndSave } from '@vyce/core/src/utils';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import {
  GetAgreementRequest,
  GetAgreementsRequest,
  RefreshPayrolleeDocRequest,
} from '@vyce/core/src/api/types';

import { AgreementVersionsDialog, AgreementVersionRequests } from './components/AgreementVersionsDialog';
import { getDocumentErrorNotification } from './components/documentErrorNotification';

interface Props {
  token: string;
  userId?: string;
  companyId?: string;
  unit?: string;
  withProfileButton?: boolean;
  getAgreementsRequest: (data: GetAgreementsRequest) => Promise<AxiosResponse>;
  downloadAgreementRequest: (data: GetAgreementRequest) => Promise<AxiosResponse>;
  // Admin-only: regenerate the document when its URL is missing on the row.
  refreshAgreementRequest?: (data: RefreshPayrolleeDocRequest) => Promise<AxiosResponse>;
  refreshMedicalRecordRequest?: (data: RefreshPayrolleeDocRequest) => Promise<AxiosResponse>;
  // Legend-only: version history and its management actions. Needs userId to be set.
  versionRequests?: AgreementVersionRequests;
}

export const Agreements: React.FC<Props> = ({
  token,
  getAgreementsRequest,
  downloadAgreementRequest,
  refreshAgreementRequest,
  refreshMedicalRecordRequest,
  versionRequests,
  userId,
  companyId,
  unit,
  withProfileButton,
}) => {
  const theme = useTheme();
  const { handleServerError, showNotification } = useContext(NotificationContext);
  const [agreements, setAgreements] = useState<Agreement[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingRowId, setLoadingRowId] = useState<string>('');
  const [versionsPaySchedule, setVersionsPaySchedule] = useState<AgreementPaySchedule | null>(null);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'created_at', sort: 'desc' }]);
  const showVersionHistory = !!(versionRequests && userId);
  const columns: GridColDef[] = [
    {
      field: 'company',
      headerName: 'Company',
      flex: 1.5,
      disableColumnMenu: true,
      sortable: false,
      valueGetter: params => params.row.company.name,
      minWidth: 120,
    },
    {
      field: 'pay_schedule',
      headerName: 'Pay Schedule',
      flex: 1.5,
      disableColumnMenu: true,
      sortable: false,
      valueGetter: params => params.row.pay_schedule.name,
      minWidth: 120,
    },
    {
      field: 'pay_scheme',
      headerName: 'Type',
      valueGetter: params => params.row?.pay_scheme?.toUpperCase(),
      flex: 1,
      disableColumnMenu: true,
      minWidth: 120,
    },
    {
      field: 'agreement_date',
      headerName: 'Agreement Date',
      flex: 1,
      disableColumnMenu: true,
      sortable: false,
      // The date printed inside the contract; null = a pre-versioning document.
      valueGetter: params =>
        params.row.pay_schedule?.agreement_date
          ? formatTableDate(params.row.pay_schedule.agreement_date)
          : '—',
      minWidth: 130,
    },
    {
      field: 'created_at',
      headerName: 'Start Date',
      flex: 1,
      disableColumnMenu: true,
      valueGetter: params => formatTableDate(params.row?.created_at),
      minWidth: 120,
    },
    {
      field: 'updated_at',
      headerName: 'End Date',
      flex: 1,
      disableColumnMenu: true,
      valueGetter: params => (params.row.active ? '' : formatTableDate(params.row?.updated_at)),
      minWidth: 120,
    },
    {
      field: 'active',
      headerName: 'Status',
      width: 150,
      type: 'boolean',
      disableColumnMenu: true,
      renderCell: params => <IsVerified verified={params.row.active} />,
    },
    {
      field: '',
      headerName: ' ',
      width: showVersionHistory ? 160 : 120,
      hideSortIcons: true,
      sortable: false,
      disableColumnMenu: true,
      renderCell: params => {
        const payScheduleId = params.row.pay_schedule.pay_schedule_id;
        const agreementLoading = loadingRowId === `${payScheduleId}:agreement`;
        const medicalLoading = loadingRowId === `${payScheduleId}:medical`;
        const showMedical = !!(params.row.pay_schedule.medical_record_url || refreshMedicalRecordRequest);
        return (
          <Box display="flex" width="100%">
            <Tooltip title="Download agreement">
              <Box display="flex">
                <AppIconButton
                  variant="paper"
                  disabled={agreementLoading}
                  onClick={() => downloadAgreement(params.row.pay_schedule)}
                >
                  {agreementLoading ? (
                    <CircularProgress color="secondary" size={24} />
                  ) : (
                    <MdOutlinePictureAsPdf color={theme.palette.secondary.main} />
                  )}
                </AppIconButton>
              </Box>
            </Tooltip>
            {showMedical && (
              <Tooltip title="Download medical record">
                <Box display="flex" ml={1}>
                  <AppIconButton
                    variant="paper"
                    disabled={medicalLoading}
                    onClick={() => downloadMedicalRecord(params.row.pay_schedule)}
                  >
                    {medicalLoading ? (
                      <CircularProgress color="secondary" size={24} />
                    ) : (
                      <MdOutlineMedicalServices color={theme.palette.secondary.main} />
                    )}
                  </AppIconButton>
                </Box>
              </Tooltip>
            )}
            {showVersionHistory && (
              <Tooltip title="Version history">
                <Box display="flex" ml={1}>
                  <AppIconButton
                    variant="paper"
                    onClick={() => setVersionsPaySchedule(params.row.pay_schedule)}
                  >
                    <MdHistory color={theme.palette.secondary.main} />
                  </AppIconButton>
                </Box>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  const getAgreements = async (sortModel: GridSortModel) => {
    try {
      setLoading(true);
      const res = await getAgreementsRequest({
        token,
        order_by: formatSortModel<Agreement>(sortModel),
        userId,
        companyId,
      });
      setAgreements(res.data.items);
      setTotal(res.data.count);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      console.error(e);
    }
  };

  const openDocument = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // 422 (incomplete worker data), 503 and 404 come with actionable copy; anything else falls through.
  const handleDocumentError = (e: any) => {
    const notification = getDocumentErrorNotification(e);
    if (notification) {
      showNotification(notification);
    } else {
      handleServerError(e);
    }
  };

  // The URL may be missing on the row — regenerate it via the refresh endpoint, then open it.
  const refreshAndOpen = async (refresh: () => Promise<AxiosResponse>, loadingKey: string) => {
    try {
      setLoadingRowId(loadingKey);
      const { data } = await refresh();
      if (data?.url) {
        openDocument(data.url);
      }
      setLoadingRowId('');
    } catch (e) {
      setLoadingRowId('');
      handleDocumentError(e);
    }
  };

  const downloadAgreement = async (paySchedule: AgreementPaySchedule) => {
    const { pay_schedule_id, agreement_url } = paySchedule;
    // New documents gateway returns a ready-to-use document URL on the row.
    if (agreement_url) {
      openDocument(agreement_url);
      return;
    }
    // No URL yet — regenerate it, then open.
    if (refreshAgreementRequest) {
      await refreshAndOpen(
        () => refreshAgreementRequest({ token, userId, companyId, payScheduleId: pay_schedule_id }),
        `${pay_schedule_id}:agreement`
      );
      return;
    }
    // Fallback for the employer flow, which still serves the agreement as a blob.
    try {
      setLoadingRowId(`${pay_schedule_id}:agreement`);
      const res = await downloadAgreementRequest({
        token,
        payScheduleId: pay_schedule_id,
        userId,
        companyId,
      });
      generateFileLinkAndSave(res);
      setLoadingRowId('');
    } catch (e) {
      setLoadingRowId('');
      handleServerError(e);
    }
  };

  const downloadMedicalRecord = async (paySchedule: AgreementPaySchedule) => {
    const { pay_schedule_id, medical_record_url } = paySchedule;
    if (medical_record_url) {
      openDocument(medical_record_url);
      return;
    }
    if (refreshMedicalRecordRequest) {
      await refreshAndOpen(
        () => refreshMedicalRecordRequest({ token, userId, companyId, payScheduleId: pay_schedule_id }),
        `${pay_schedule_id}:medical`
      );
    }
  };

  const handleSortModelChange = (newModel: GridSortModel) => {
    if (isEqual(newModel, sortModel)) {
      return;
    }
    setSortModel(newModel);
    getAgreements(newModel);
  };

  useEffect(() => {
    getAgreements(sortModel);
  }, []);

  return (
    <>
      <AppDataGrid
        noPaper
        rows={agreements}
        height={'calc(100vh - 200px)'}
        getRowId={row => row.pay_schedule.pay_schedule_id}
        loading={loading}
        columns={columns}
        rowCount={total}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        pageSize={GRID_PAGE_SIZE}
        rowsPerPageOptions={[GRID_PAGE_SIZE]}
        disableSelectionOnClick
        unit={unit}
        withProfileButton={withProfileButton}
      />

      {showVersionHistory && versionsPaySchedule && (
        <AgreementVersionsDialog
          open={!!versionsPaySchedule}
          token={token}
          userId={userId as string}
          paySchedule={versionsPaySchedule}
          requests={versionRequests as AgreementVersionRequests}
          handleClose={() => setVersionsPaySchedule(null)}
          onDocumentChanged={() => getAgreements(sortModel)}
        />
      )}
    </>
  );
};
